import { useState } from 'react';
import { CheckCircle2, CornerDownRight, CalendarDays, Plus, GripVertical } from 'lucide-react';
import { STATUSES, dueInDays } from './lib';
import { useBiz } from './BizContext';
import { Tag, FlagChip, AvatarStack, ImpactEffort, StaleChip, fmtDate } from './bits';

export function InitiativeCard({ item, onOpen, delay = 0, draggable }) {
  const { goalMap } = useBiz();
  const goal = goalMap[item.goal_id];
  const done = item.status === 'done';
  const due = dueInDays(item);
  const dueTone = due == null ? 'var(--ink-3)' : due < 0 ? 'var(--rose-deep)' : due <= 14 ? 'var(--apricot-deep)' : 'var(--ink-3)';
  return (
    <div role="button" tabIndex={0} onClick={() => onOpen(item.id)} onKeyDown={(e) => e.key === 'Enter' && onOpen(item.id)} data-testid={`biz-card-${item.id}`}
      draggable={draggable} onDragStart={(e) => { e.dataTransfer.setData('text/biz-item', item.id); e.dataTransfer.effectAllowed = 'move'; }}
      className={`biz-card biz-card-hover biz-rise text-left w-full p-4 cursor-pointer ${done ? 'opacity-75' : ''} ${draggable ? 'active:cursor-grabbing' : ''}`} style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start gap-2">
        {done ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--sage-deep)' }} /> : draggable && <GripVertical className="w-4 h-4 mt-0.5 shrink-0 -ml-1" style={{ color: 'var(--line-2)' }} />}
        <h3 className="text-[15px] leading-snug flex-1">{item.title}</h3>
      </div>
      {goal && <p className="mt-1.5 text-xs flex items-center gap-1 truncate" style={{ color: 'var(--ink-3)' }}><CornerDownRight className="w-3 h-3 shrink-0" /> <span className="truncate">{goal.title}</span></p>}
      {(item.tags.length > 0 || item.flags.length > 0 || (!done && item.updated_at)) && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {item.tags.map((t) => <Tag key={t} k={t} />)}
          {item.flags.map((f) => <FlagChip key={f} k={f} />)}
          <StaleChip item={item} />
        </div>
      )}
      {item.waiting_on && !done && <p className="mt-2 text-xs italic" style={{ color: 'var(--rose-deep)' }}>Waiting on: {item.waiting_on}</p>}
      <div className="flex items-center justify-between mt-3 pt-3 gap-2" style={{ borderTop: '1px solid var(--line)' }}>
        <div className="flex items-center gap-2 min-w-0">
          <AvatarStack entity={item} />
          {done ? (
            <span className="text-xs" style={{ color: 'var(--sage-deep)' }}>Done {fmtDate(item.completed_at)}</span>
          ) : item.due ? (
            <span className="text-xs inline-flex items-center gap-1 whitespace-nowrap" style={{ color: dueTone }}>
              <CalendarDays className="w-3 h-3" /> {due < 0 ? `${-due}d overdue` : due === 0 ? 'Due today' : `Due ${fmtDate(item.due)}`}
            </span>
          ) : <span className="text-xs" style={{ color: 'var(--ink-3)' }}>No date</span>}
        </div>
        <ImpactEffort impact={item.impact} effort={item.effort} />
      </div>
    </div>
  );
}

export function BizBoard({ items, onOpen, onMove, onNew }) {
  const [over, setOver] = useState(null);
  const drop = (e, status) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/biz-item');
    setOver(null);
    if (id) onMove(id, status);
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5" data-testid="biz-board">
      {STATUSES.map((s) => {
        const col = items.filter((i) => i.status === s.key);
        return (
          <section key={s.key} className="min-w-0" data-testid={`biz-column-${s.key}`}
            onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; if (over !== s.key) setOver(s.key); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setOver(null); }} onDrop={(e) => drop(e, s.key)}>
            <header className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: s.dot }} />
                <h2 className="text-lg">{s.label}</h2>
                <span className="biz-count" data-testid={`biz-column-count-${s.key}`}>{col.length}</span>
              </div>
              <button type="button" onClick={() => onNew({ status: s.key })} className="p-1.5 rounded-full hover:bg-black/5" title={`Add to ${s.label}`} data-testid={`biz-column-add-${s.key}`}><Plus className="w-4 h-4" style={{ color: 'var(--ink-3)' }} /></button>
            </header>
            <div className="flex flex-col gap-3 rounded-2xl p-2.5 min-h-[160px] transition-colors" style={{ background: over === s.key ? 'var(--sage-soft)' : 'rgba(42,39,34,0.03)', outline: over === s.key ? '2px dashed var(--sage)' : 'none' }}>
              {col.map((item, idx) => <InitiativeCard key={item.id} item={item} onOpen={onOpen} delay={Math.min(idx, 8) * 40} draggable />)}
              {col.length === 0 && <p className="text-[13px] text-center py-12 px-4 leading-relaxed" style={{ color: 'var(--ink-3)' }}>{s.hint}.<br />Drop a card here or press +</p>}
            </div>
          </section>
        );
      })}
    </div>
  );
}
